import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Invoice, Payment } from '../types';
import { formatCurrency } from './formatters';
import { formatArgentinaDate } from './dateUtils';

// Company info
const COMPANY_NAME = 'PRODUCTEX';
const COMPANY_LEGAL_NAME = 'RGX GROUP SRL';
const COMPANY_ADDRESS = 'Nuñez 1567, Ciudad Autónoma de Buenos Aires';
const COMPANY_EMAIL = 'hola@productex.com.ar';
const COMPANY_PHONE = '+5491154731466';
const COMPANY_CUIT = '30-71746602-7';

// Función para convertir números a palabras en español
const numeroALetras = (numero: number): string => {
  const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
  const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  if (numero === 0) return 'cero';
  if (numero < 0) return 'menos ' + numeroALetras(Math.abs(numero));

  let letras = '';

  // Millones
  if (numero >= 1000000) {
    const millones = Math.floor(numero / 1000000);
    numero %= 1000000;
    if (millones === 1) {
      letras += 'un millón ';
    } else {
      letras += numeroALetras(millones) + ' millones ';
    }
  }

  // Miles
  if (numero >= 1000) {
    const miles = Math.floor(numero / 1000);
    numero %= 1000;
    if (miles === 1) {
      letras += 'mil ';
    } else {
      letras += numeroALetras(miles) + ' mil ';
    }
  }

  // Centenas
  if (numero >= 100) {
    if (numero === 100) {
      letras += 'cien ';
    } else {
      letras += centenas[Math.floor(numero / 100)] + ' ';
    }
    numero %= 100;
  }

  // Decenas y unidades
  if (numero > 0) {
    if (numero < 10) {
      letras += unidades[numero];
    } else if (numero < 20) {
      letras += especiales[numero - 10];
    } else {
      const unidad = numero % 10;
      const decena = Math.floor(numero / 10);
      if (unidad === 0) {
        letras += decenas[decena];
      } else {
        letras += decenas[decena] + ' y ' + unidades[unidad];
      }
    }
  }

  return letras.trim();
};

export const generateInvoicePDF = async (invoice: Invoice, entityName: string) => {
  // Skip PDF generation for supplier invoices
  if (invoice.entity_type === 'supplier') {
    console.log('Skipping PDF generation for supplier invoice');
    return;
  }
  
  // Skip PDF generation for client invoices with types A, B, C
  if (invoice.entity_type === 'client' && (invoice.type === 'A' || invoice.type === 'B' || invoice.type === 'C')) {
    console.log('Skipping PDF generation for client invoice type', invoice.type);
    return;
  }
  
  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`FACTURA ${invoice.type || 'X'}`, 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`E-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Invoice details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Calculate client data height dynamically
  let clientHeight = 40; // Base height
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Get client details from the invoice if available
  const client = invoice.entity_id ? await getClientDetails(invoice.entity_id) : null;
  
  if (client) {
    let yPos = startY + 20;
    
    // Nombre del cliente
    if (client.name) {
      doc.text(client.name, 15, yPos);
      yPos += 5;
    }
    
    // Razón social
    if (client.business_name) {
      doc.text(client.business_name, 15, yPos);
      yPos += 5;
    }
    
    // Dirección
    if (client.address) {
      doc.text(client.address, 15, yPos);
      yPos += 5;
    }
    
    // Ciudad y Provincia
    if (client.city || client.province) {
      const location = [client.city, client.province].filter(Boolean).join(', ');
      if (location) {
        doc.text(location, 15, yPos);
        yPos += 5;
      }
    }
    
    // Código postal
    if (client.postal_code) {
      doc.text(`CP: ${client.postal_code}`, 15, yPos);
      yPos += 5;
    }
    
    // CUIT
    if (client.cuit) {
      doc.text(`CUIT: ${client.cuit}`, 15, yPos);
      yPos += 5;
    }
    
    // Condición fiscal
    if (client.tax_condition) {
      const taxConditionMap: Record<string, string> = {
        'RI': 'Responsable Inscripto',
        'MT': 'Monotributista',
        'EX': 'Exento',
        'CF': 'Consumidor Final',
        'NC': 'No Categorizado'
      };
      
      doc.text(`IVA: ${taxConditionMap[client.tax_condition] || client.tax_condition}`, 15, yPos);
      yPos += 5;
    }
    
    // Teléfono
    if (client.phone) {
      doc.text(`Tel: ${client.phone}`, 15, yPos);
      yPos += 5;
    }
    
    // Email
    if (client.email) {
      doc.text(`Email: ${client.email}`, 15, yPos);
      yPos += 5;
    }
    
    // Observaciones
    if (client.notes) {
      doc.text(`Obs: ${client.notes}`, 15, yPos);
      yPos += 5;
    }

    // Calculate actual height needed for client data
    clientHeight = Math.max(clientHeight, yPos - startY + 10);
  } else {
    doc.text(entityName, 15, startY + 20);
  }
  
  // Draw rectangle for client data with calculated height
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, clientHeight);
  
  // Draw rectangle for invoice data with same height as client data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, clientHeight);
  
  // Right column - Invoice data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Factura', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${invoice.number}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(invoice.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Add due date if available
  if (invoice.due_date) {
    doc.text(`Vencimiento: ${formatArgentinaDate(invoice.due_date)}`, doc.internal.pageSize.width / 2, startY + 40);
  }
  
  // Items table
  doc.autoTable({
    startY: startY + clientHeight + 10,
    head: [['DESCRIPCIÓN', 'CANTIDAD', 'VALOR UNITARIO', 'IVA', 'TOTAL']],
    body: invoice.items.map(item => [
      item.description,
      item.quantity.toString(),
      formatCurrency(item.unit_price),
      `${item.tax_rate}%`,
      formatCurrency(item.total),
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 25, halign: 'right' }, // Increased width for CANTIDAD
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 20, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
    theme: 'plain',
    margin: { left: 10, right: 10 },
  });
  
  // Totals
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  
  // Add separator line before totals
  doc.setDrawColor(0);
  doc.line(10, finalY - 5, doc.internal.pageSize.width - 10, finalY - 5);
  
  // Right-aligned totals
  const rightMargin = doc.internal.pageSize.width - 10;
  
  doc.setFontSize(9);
  doc.text('SUBTOTAL', rightMargin - 80, finalY);
  doc.text(formatCurrency(invoice.subtotal), rightMargin, finalY, { align: 'right' });
  
  doc.text('IVA', rightMargin - 80, finalY + 7);
  doc.text(formatCurrency(invoice.tax), rightMargin, finalY + 7, { align: 'right' });
  
  // Add separator line before total
  doc.setDrawColor(0);
  doc.line(rightMargin - 100, finalY + 12, rightMargin, finalY + 12);
  
  // Total in bold
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', rightMargin - 80, finalY + 20);
  doc.text(formatCurrency(invoice.total), rightMargin, finalY + 20, { align: 'right' });
  
  // Save the PDF
  doc.save(`factura-${invoice.number}.pdf`);
};

export const generatePaymentPDF = async (payment: Payment, entityName: string) => {
  // Check if this is a supplier payment by looking at the related invoice
  const invoice = payment.invoice_id ? 
    await getInvoiceDetails(payment.invoice_id) : 
    null;
  
  // Skip PDF generation for supplier payments
  if (invoice && invoice.entity_type === 'supplier') {
    console.log('Skipping PDF generation for supplier payment');
    return;
  }
  
  const doc = new jsPDF();
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBO', 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`e-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Receipt details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Calculate client data height dynamically
  let clientHeight = 40; // Base height
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Get client details from the invoice if available
  const client = invoice?.entity_id ? await getClientDetails(invoice.entity_id) : null;
  
  if (client) {
    let yPos = startY + 20;
    
    // Nombre del cliente
    if (client.name) {
      doc.text(client.name, 15, yPos);
      yPos += 5;
    }
    
    // Razón social
    if (client.business_name) {
      doc.text(client.business_name, 15, yPos);
      yPos += 5;
    }
    
    // Dirección
    if (client.address) {
      doc.text(client.address, 15, yPos);
      yPos += 5;
    }
    
    // Ciudad y Provincia
    if (client.city || client.province) {
      const location = [client.city, client.province].filter(Boolean).join(', ');
      if (location) {
        doc.text(location, 15, yPos);
        yPos += 5;
      }
    }
    
    // Código postal
    if (client.postal_code) {
      doc.text(`CP: ${client.postal_code}`, 15, yPos);
      yPos += 5;
    }
    
    // CUIT
    if (client.cuit) {
      doc.text(`CUIT: ${client.cuit}`, 15, yPos);
      yPos += 5;
    }
    
    // Condición fiscal
    if (client.tax_condition) {
      const taxConditionMap: Record<string, string> = {
        'RI': 'Responsable Inscripto',
        'MT': 'Monotributista',
        'EX': 'Exento',
        'CF': 'Consumidor Final',
        'NC': 'No Categorizado'
      };
      
      doc.text(`IVA: ${taxConditionMap[client.tax_condition] || client.tax_condition}`, 15, yPos);
      yPos += 5;
    }
    
    // Teléfono
    if (client.phone) {
      doc.text(`Tel: ${client.phone}`, 15, yPos);
      yPos += 5;
    }
    
    // Email
    if (client.email) {
      doc.text(`Email: ${client.email}`, 15, yPos);
      yPos += 5;
    }
    
    // Observaciones
    if (client.notes) {
      doc.text(`Obs: ${client.notes}`, 15, yPos);
      yPos += 5;
    }

    // Calculate actual height needed for client data
    clientHeight = Math.max(clientHeight, yPos - startY + 10);
  } else {
    doc.text(entityName, 15, startY + 20);
  }
  
  // Draw rectangle for client data with calculated height
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, clientHeight);
  
  // Draw rectangle for receipt data with same height as client data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, clientHeight);
  
  // Right column - Receipt data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Recibo', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${payment.id.substring(0, 8)}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(payment.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Payment details
  const methodLabels = {
    cash: 'Efectivo',
    transfer: 'Transferencia',
    card: 'Tarjeta',
    check: 'Cheque',
    mercadopago: 'MercadoPago',
  };
  
  // Draw horizontal line below the header
  doc.line(10, startY + clientHeight + 10, doc.internal.pageSize.width - 10, startY + clientHeight + 10);
  
  // Payment information
  const paymentY = startY + clientHeight + 20;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBIMOS LA SUMA DE:', 10, paymentY);
  
  // Amount in numbers
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(payment.amount), 10, paymentY + 10);
  
  // Amount in words
  doc.setFont('helvetica', 'bold');
  doc.text('SON PESOS:', 10, paymentY + 25);
  doc.setFont('helvetica', 'normal');
  
  // Split long text into multiple lines if needed
  const amountInWords = numeroALetras(payment.amount).toUpperCase();
  const maxWidth = doc.internal.pageSize.width - 20; // 10px margin on each side
  const lines = doc.splitTextToSize(amountInWords, maxWidth);
  doc.text(lines, 10, paymentY + 35);
  
  // Calculate next Y position based on number of lines
  const nextY = paymentY + 35 + (lines.length * 5);
  
  // Payment method
  doc.setFont('helvetica', 'bold');
  doc.text('FORMA DE PAGO:', 10, nextY + 10);
  doc.setFont('helvetica', 'normal');
  doc.text(methodLabels[payment.method], 10, nextY + 20);
  
  // Reference if available
  let totalY = nextY + 20;
  if (payment.reference) {
    doc.setFont('helvetica', 'bold');
    doc.text('REFERENCIA:', 10, totalY + 10);
    doc.setFont('helvetica', 'normal');
    doc.text(payment.reference, 10, totalY + 20);
    totalY += 20;
  }
  
  // Total
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL COBRADO:', 10, totalY + 10);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(payment.amount), 10, totalY + 20);
  
  // Save the PDF
  doc.save(`recibo-${payment.id.substring(0, 8)}.pdf`);
};

export const generateDeliveryNotePDF = async (deliveryNote: any) => {
  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Company header - Left side
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_NAME, 10, 20);
  
  // Document type below company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('REMITO', 10, 30);
  
  // Company details - Right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_LEGAL_NAME, doc.internal.pageSize.width - 10, 15, { align: 'right' });
  doc.text(COMPANY_ADDRESS, doc.internal.pageSize.width - 10, 20, { align: 'right' });
  doc.text(`Teléfono: ${COMPANY_PHONE}`, doc.internal.pageSize.width - 10, 25, { align: 'right' });
  doc.text(`e-mail: ${COMPANY_EMAIL}`, doc.internal.pageSize.width - 10, 30, { align: 'right' });
  doc.text(`C.U.I.T. ${COMPANY_CUIT}`, doc.internal.pageSize.width - 10, 35, { align: 'right' });
  
  // Document validity notice - Center
  doc.setFontSize(10);
  doc.setFont('helvetica');
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', doc.internal.pageSize.width / 2, 45, { align: 'center' });
  
  // Draw horizontal line
  doc.line(10, 55, doc.internal.pageSize.width - 10, 55);
  
  // Client and Delivery Note details in a table-like structure
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Create a table with two columns
  const startY = 65;
  
  // Draw rectangle for client data
  doc.rect(10, startY, doc.internal.pageSize.width / 2 - 15, 40);
  
  // Draw rectangle for delivery note data
  doc.rect(doc.internal.pageSize.width / 2 - 5, startY, doc.internal.pageSize.width / 2 - 5, 40);
  
  // Left column - Client data
  doc.text('Datos del Cliente', 15, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  let yPos = startY + 20;
  doc.text(deliveryNote.clientName, 15, yPos);
  yPos += 5;
  
  if (deliveryNote.clientAddress) {
    doc.text(deliveryNote.clientAddress, 15, yPos);
    yPos += 5;
  }
  
  if (deliveryNote.clientCity || deliveryNote.clientProvince) {
    const location = [
      deliveryNote.clientCity,
      deliveryNote.clientProvince
    ].filter(Boolean).join(', ');
    doc.text(location, 15, yPos);
    yPos += 5;
  }
  
  if (deliveryNote.clientCuit) {
    doc.text(`CUIT: ${deliveryNote.clientCuit}`, 15, yPos);
  }
  
  // Right column - Delivery Note data
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Remito', doc.internal.pageSize.width / 2, startY + 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nro.: ${deliveryNote.number}`, doc.internal.pageSize.width / 2, startY + 20);
  doc.text(`Fecha: ${formatArgentinaDate(deliveryNote.date)}`, doc.internal.pageSize.width / 2, startY + 30);
  
  // Carrier information
  const carrierY = startY + 50;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Datos del Transportista', 10, carrierY);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Transportista: ${deliveryNote.carrierName || 'No especificado'}`, 10, carrierY + 10);
  
  // Items table
  doc.autoTable({
    startY: carrierY + 20,
    head: [['DESCRIPCIÓN', 'CANTIDAD']],
    body: deliveryNote.items.map((item: any) => [
      item.description,
      item.quantity.toString(),
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 25, halign: 'right' },
    },
    theme: 'plain',
    margin: { left: 10, right: 10 },
  });
  
  // Signatures
  const finalY = (doc as any).lastAutoTable.finalY + 30;
  
  // Draw signature lines
  doc.line(30, finalY, 110, finalY); // Delivered by signature line
  doc.line(doc.internal.pageSize.width - 110, finalY, doc.internal.pageSize.width - 30, finalY); // Received by signature line
  
  // Add signature labels
  doc.setFontSize(9);
  doc.text(`Entregado por: ${deliveryNote.deliveredBy || '___________________'}`, 30, finalY + 20);
  doc.text('Recibido en conformidad por: _____________________', doc.internal.pageSize.width - 110, finalY + 20);
  
  // Legal warning footer
  const footerY = finalY + 40;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  const legalText = [
    'La firma del presente constituye plena prueba de conformidad y recepción satisfactoria de la',
    'mercaderia detallada en cuanto a calidad y cantidad conforme a lo pactado.',
  ];
  
  let currentY = footerY;
  legalText.forEach((line, index) => {
    doc.text(line, doc.internal.pageSize.width / 2, currentY + (index * 4), { align: 'center' });
  });
  
  // Save the PDF
  doc.save(`remito-${deliveryNote.number}.pdf`);
};

// Helper function to get invoice details
async function getInvoiceDetails(invoiceId: string) {
  try {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching invoice details:', error);
    return null;
  }
}

// Helper function to get client details
async function getClientDetails(clientId: string) {
  try {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single();
    
    if (error) {
      console.error('Error fetching client details:', error);
      return null;
    }
    
    console.log('Client data fetched:', data); // Para debug
    return data;
  } catch (error) {
    console.error('Error fetching client details:', error);
    return null;
  }
}